/**
 * Los totales de canjes, calculados en UN solo sitio.
 *
 * Existe por un error concreto (19-ago): el mismo filtro producía tres cifras distintas de
 * "canjes" según dónde se mirara —la pantalla de la bandeja, la hoja `Resumen` del Excel y el
 * HTML descargable— porque cada uno los sumaba por su cuenta y solo uno excluía los anulados.
 * Con 2 registros vigentes y 1 anulado de 10 canjes, la pantalla decía 15 y el HTML 5.
 *
 * Eso no es un detalle de presentación: los dos archivos salen del mismo botón, se le mandan al
 * mismo cliente y terminan en una liquidación. Quien recibe los dos no tiene forma de saber cuál
 * es el bueno.
 *
 * Regla: **cualquier pantalla o reporte que muestre "cuántos canjes hay" llama a este módulo.**
 * Si hace falta una cifra nueva, se agrega acá, no en el sitio que la necesita.
 *
 * Módulo puro: sin red, sin `Date`, sin Supabase. Solo `CanjeRow` como tipo.
 */
import type { CanjeRow } from '../api/canjes';

/**
 * Los registros que cuentan.
 *
 * **Anular es la forma de eliminar en este módulo**: el canje no se borra —queda a la vista con su
 * motivo y su autor, que es justamente la evidencia de que se anuló— pero SALE de los conteos.
 * Sumarlos le entregaría al cliente un número de canjes más alto que el que de verdad se entregó
 * en tienda.
 */
export function registrosVigentes(rows: CanjeRow[]): CanjeRow[] {
  return rows.filter((r) => r.estado === 'registrado');
}

/**
 * Las banderas de un registro, ya en texto.
 *
 * Vive acá porque la pintaban por separado el Excel y el HTML, con los mismos tres textos escritos
 * dos veces. Una bandera nueva se agregaba en uno y se olvidaba en el otro.
 *
 * Ninguna es un error: son la calidad del dato a la vista. Un registro tardío fuera de programación
 * puede ser legítimo —trabajó otro día, o no la dejaron usar el celular en sala—.
 */
export function banderasDeCanje(r: CanjeRow): string[] {
  const out: string[] = [];
  if (r.fueraDeProgramacion) out.push('Fuera de programación');
  if (r.registroTardio) out.push('Registro tardío');
  if (r.sinAsistencia) out.push('Sin asistencia');
  return out;
}

/** Unidades de premio entregadas en un registro, sumando todas sus líneas. */
export function unidadesDePremio(r: CanjeRow): number {
  return r.lineas.reduce((s, l) => s + l.premios.reduce((u, p) => u + p.units, 0), 0);
}

export interface TotalesGenerales {
  /** Registros vigentes. Los anulados NO están aquí. */
  registros: number;
  canjes: number;
  /** Anulados del periodo. Se informa aparte justamente porque no suma en nada más. */
  anulados: number;
  conBandera: number;
  montoTotal: number;
  /** Denominador del ticket promedio: no todo canje lleva monto anotado. */
  registrosConMonto: number;
  /** `null` cuando ningún registro trae monto. Ver el porqué abajo. */
  ticketPromedio: number | null;
  clientesDistintos: number;
}

export function totalesGenerales(rows: CanjeRow[]): TotalesGenerales {
  const vigentes = registrosVigentes(rows);
  const conMonto = vigentes.filter((r) => r.montoCompra !== null);
  const montoTotal = conMonto.reduce((s, r) => s + (r.montoCompra ?? 0), 0);

  // El mismo cliente tecleado con otra caja o con espacios de más es el mismo cliente: si no se
  // normaliza, "ana torres" y "Ana Torres " cuentan como dos y el indicador infla la cobertura.
  // Sigue siendo un conteo por nombre tecleado, no por documento — es lo único que hay.
  //
  // `clienteNombre` se lee con `?? ''` y NO es defensa de más: la 0030 le quitó el NOT NULL porque
  // hay dinámicas que no piden el nombre ("en un raspa y gana la evidencia es la boleta"), y
  // `CanjeForm` manda `null` en esas. El tipo de `CanjeRow` decía `string` y mentía; leerlo sin
  // guarda reventaba la generación del reporte entero para esas campañas.
  const clientes = new Set(
    vigentes
      .map((r) => (r.clienteNombre ?? '').trim().toLocaleLowerCase('es-PE'))
      .filter((n) => n !== ''),
  );

  return {
    registros: vigentes.length,
    canjes: vigentes.reduce((s, r) => s + r.totalCanjes, 0),
    anulados: rows.filter((r) => r.estado === 'anulado').length,
    conBandera: vigentes.filter((r) => banderasDeCanje(r).length > 0).length,
    montoTotal,
    registrosConMonto: conMonto.length,
    // Dividir entre cero da NaN, y NaN se imprimiría tal cual en el archivo que abre el cliente.
    // El caso no es raro ni defensivo: en una dinámica de producto NINGÚN registro lleva monto,
    // así que este `null` —que la vista pinta como "—"— es el resultado normal de esa campaña.
    ticketPromedio: conMonto.length === 0 ? null : montoTotal / conMonto.length,
    clientesDistintos: clientes.size,
  };
}
